import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Markup master (Masters → Markups). Pricing markup rules applied to quotes,
 * either as a percent of cost or a flat per-pax amount. See ADR-0016 /
 * ADR-0020. The `appliesTo` JSON scope column is NOT editable from this admin
 * surface yet (promoted to real join tables when the quote engine needs it);
 * it is intentionally omitted from the write contract.
 */

/** Mirrors the Prisma `MarkupKind` enum. */
export const MarkupKindSchema = z.enum(['PERCENT', 'FLAT']);
export type MarkupKind = z.infer<typeof MarkupKindSchema>;

export const MarkupListItemSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  kind: MarkupKindSchema,
  /** PERCENT: 12.5 means 12.5%. FLAT: raw amount in the markup's currency. */
  value: z.number(),
  /** Loose FK to `currencies` (no Prisma relation); resolve label client-side. */
  currencyId: UuidSchema.nullable(),
  validFrom: IsoDateTimeSchema.nullable(),
  validUntil: IsoDateTimeSchema.nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type MarkupListItem = z.infer<typeof MarkupListItemSchema>;

export const MarkupListQuerySchema = PaginationQuerySchema.extend({
  kind: MarkupKindSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type MarkupListQuery = z.infer<typeof MarkupListQuerySchema>;

export const MarkupListResponseSchema = paginatedResponseSchema(MarkupListItemSchema);
export type MarkupListResponse = z.infer<typeof MarkupListResponseSchema>;

export const MarkupDetailSchema = MarkupListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type MarkupDetail = z.infer<typeof MarkupDetailSchema>;

const MarkupCoreFieldsSchema = z
  .object({
    code: MasterCodeSchema,
    name: z.string().trim().min(1, 'Name is required').max(120),
    kind: MarkupKindSchema,
    value: z.number().min(0, 'Value must be zero or more'),
    currencyId: UuidSchema.nullable().optional(),
    validFrom: IsoDateTimeSchema.nullable().optional(),
    validUntil: IsoDateTimeSchema.nullable().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((v) => v.kind !== 'PERCENT' || v.value <= 100, {
    message: 'A percent markup cannot exceed 100',
    path: ['value'],
  })
  .refine(
    (v) =>
      !v.validFrom ||
      !v.validUntil ||
      new Date(v.validFrom).getTime() <= new Date(v.validUntil).getTime(),
    { message: 'Valid-until must be on or after valid-from', path: ['validUntil'] },
  );

/**
 * Create + Update share the same effects-wrapped object. Update is NOT a simple
 * `.partial()` here because the schema is a ZodEffects (refinements); callers
 * send the full editable field set, which the form always provides.
 */
export const CreateMarkupRequestSchema = MarkupCoreFieldsSchema;
export type CreateMarkupRequest = z.infer<typeof CreateMarkupRequestSchema>;

export const UpdateMarkupRequestSchema = MarkupCoreFieldsSchema;
export type UpdateMarkupRequest = z.infer<typeof UpdateMarkupRequestSchema>;
